import { useState, useEffect } from 'react'
import { Play, Pause, Edit, Trash2, Plus, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react'
import api from '../services/api'
import axios from 'axios'

// Create a separate axios instance for cron API
const cronAPI = axios.create({
  baseURL: '/api/admin/cron-jobs',
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add interceptor to include JWT token in all cron API requests
cronAPI.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const CATEGORY_COLORS = {
  daily_pipeline: 'blue',
  ml_training: 'purple',
  model_management: 'green',
  monitoring: 'yellow',
  infrastructure: 'cyan',
  maintenance: 'orange',
  general: 'gray'
}

// Convert cron expression to human-readable format
const cronToReadable = (cron) => {
  if (!cron) return 'Invalid schedule'

  const parts = cron.trim().split(' ')
  if (parts.length !== 5) return cron // Return original if invalid

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts

  // Common patterns
  if (cron === '0 0 * * *') return 'Daily at midnight (12:00 AM)'
  if (cron === '0 1 * * *') return 'Daily at 1:00 AM'
  if (cron === '0 2 * * *') return 'Daily at 2:00 AM'
  if (cron === '0 3 * * *') return 'Daily at 3:00 AM'
  if (cron === '0 4 * * *') return 'Daily at 4:00 AM'
  if (cron === '0 5 * * *') return 'Daily at 5:00 AM'
  if (cron === '0 17 * * 1-5') return 'Weekdays at 5:00 PM'
  if (cron === '0 18 * * 1-5') return 'Weekdays at 6:00 PM'
  if (cron === '0 0 * * 0') return 'Weekly on Sunday at midnight'
  if (cron === '0 0 1 * *') return 'Monthly on 1st at midnight'

  // Build readable string
  let readable = ''

  // Time part
  if (minute === '0' && hour !== '*') {
    const hourNum = parseInt(hour)
    const ampm = hourNum >= 12 ? 'PM' : 'AM'
    const hour12 = hourNum % 12 || 12
    readable = `at ${hour12}:00 ${ampm}`
  } else if (minute !== '*' && hour !== '*') {
    const hourNum = parseInt(hour)
    const ampm = hourNum >= 12 ? 'PM' : 'AM'
    const hour12 = hourNum % 12 || 12
    readable = `at ${hour12}:${minute.padStart(2, '0')} ${ampm}`
  } else {
    readable = cron
  }

  // Frequency part
  if (dayOfWeek === '1-5') {
    readable = `Weekdays ${readable}`
  } else if (dayOfWeek !== '*') {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    readable = `${days[parseInt(dayOfWeek)]} ${readable}`
  } else if (dayOfMonth !== '*') {
    readable = `Monthly on day ${dayOfMonth} ${readable}`
  } else if (hour !== '*' && minute !== '*') {
    readable = `Daily ${readable}`
  }

  return readable
}

const CronJobs = () => {
  const [jobs, setJobs] = useState([])
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedJob, setSelectedJob] = useState(null)
  const [executions, setExecutions] = useState([])
  const [showExecutions, setShowExecutions] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingJob, setEditingJob] = useState(null)
  const [scheduleMode, setScheduleMode] = useState('preset') // 'preset' or 'custom'
  const [customSchedule, setCustomSchedule] = useState({
    frequency: 'daily',
    hour: '0',
    minute: '0',
    dayOfWeek: '*',
    dayOfMonth: '*'
  })
  // Job progress tracking
  const [runningJob, setRunningJob] = useState(null)
  const [jobProgress, setJobProgress] = useState(null)
  const [showProgressModal, setShowProgressModal] = useState(false)

  useEffect(() => {
    loadJobs()
    loadCategories()
  }, [selectedCategory])

  const loadJobs = async () => {
    try {
      const params = selectedCategory ? { category: selectedCategory } : {}
      const response = await cronAPI.get('/jobs', { params })
      setJobs(response.data.jobs)
    } catch (error) {
      console.error('Error loading jobs:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      const response = await cronAPI.get('/categories')
      setCategories(response.data.categories)
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const toggleJob = async (jobId) => {
    try {
      await cronAPI.post(`/jobs/${jobId}/toggle`)
      loadJobs()
    } catch (error) {
      alert('Failed to toggle job: ' + error.message)
    }
  }

  const runJob = async (jobId) => {
    if (!confirm('Are you sure you want to manually trigger this job?')) return

    const job = jobs.find(j => j.id === jobId)

    try {
      // Start the job (returns immediately with execution_id)
      const response = await cronAPI.post(`/jobs/${jobId}/run`)

      if (response.data.status === 'started') {
        // Open progress modal and start polling
        setRunningJob({
          id: jobId,
          name: response.data.job_name,
          executionId: response.data.execution_id,
          startTime: new Date()
        })
        setJobProgress({
          status: 'running',
          output: '',
          error: '',
          duration: 0
        })
        setShowProgressModal(true)

        // Start polling for progress
        pollJobProgress(jobId, response.data.execution_id)
      }
    } catch (error) {
      alert('Failed to start job: ' + error.message)
    }
  }

  const pollJobProgress = async (jobId, executionId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await cronAPI.get(`/jobs/${jobId}/executions`)
        const execution = response.data.executions.find(e => e.id === executionId)

        if (execution) {
          setJobProgress({
            status: execution.status,
            output: execution.output || '',
            error: execution.error || '',
            duration: execution.duration_seconds || Math.floor((new Date() - runningJob?.startTime) / 1000)
          })

          // Stop polling if job completed
          if (execution.status !== 'running') {
            clearInterval(pollInterval)
            loadJobs() // Refresh the jobs list
          }
        }
      } catch (error) {
        console.error('Error polling job progress:', error)
      }
    }, 2000) // Poll every 2 seconds

    // Store interval ID so we can clear it when modal closes
    setRunningJob(prev => ({ ...prev, pollInterval }))
  }

  const closeProgressModal = () => {
    // Clear polling interval if still running
    if (runningJob?.pollInterval) {
      clearInterval(runningJob.pollInterval)
    }
    setShowProgressModal(false)
    setRunningJob(null)
    setJobProgress(null)
    loadJobs()
  }

  const deleteJob = async (jobId, jobName) => {
    if (!confirm(`Are you sure you want to delete "${jobName}"? This action cannot be undone.`)) return

    try {
      await cronAPI.delete(`/jobs/${jobId}`)
      alert('Job deleted successfully')
      loadJobs()
    } catch (error) {
      alert('Failed to delete job: ' + error.message)
    }
  }

  const viewExecutions = async (job) => {
    try {
      setSelectedJob(job)
      const response = await cronAPI.get(`/jobs/${job.id}/executions`)
      setExecutions(response.data.executions)
      setShowExecutions(true)
    } catch (error) {
      alert('Failed to load executions: ' + error.message)
    }
  }

  const editJob = (job) => {
    setEditingJob({ ...job })
    setScheduleMode('preset')
    setShowEditModal(true)
  }

  // Generate cron expression from custom schedule
  const generateCronFromCustom = () => {
    const { frequency, hour, minute, dayOfWeek, dayOfMonth } = customSchedule

    switch (frequency) {
      case 'daily':
        return `${minute} ${hour} * * *`
      case 'weekdays':
        return `${minute} ${hour} * * 1-5`
      case 'weekends':
        return `${minute} ${hour} * * 0,6`
      case 'weekly':
        return `${minute} ${hour} * * ${dayOfWeek}`
      case 'monthly':
        return `${minute} ${hour} ${dayOfMonth} * *`
      default:
        return `${minute} ${hour} * * *`
    }
  }

  // Apply preset schedule
  const applyPreset = (cronExpr) => {
    setEditingJob({ ...editingJob, schedule: cronExpr })
  }

  // Apply custom schedule
  const applyCustomSchedule = () => {
    const cron = generateCronFromCustom()
    setEditingJob({ ...editingJob, schedule: cron })
  }

  const updateJob = async () => {
    try {
      await cronAPI.put(`/jobs/${editingJob.id}`, {
        name: editingJob.name,
        description: editingJob.description,
        schedule: editingJob.schedule,
        command: editingJob.command,
        category: editingJob.category,
        timeout_seconds: editingJob.timeout_seconds
      })
      alert('Job updated successfully')
      setShowEditModal(false)
      loadJobs()
    } catch (error) {
      alert('Failed to update job: ' + error.message)
    }
  }

  const getCategoryColor = (category) => {
    const color = CATEGORY_COLORS[category] || 'gray'
    return {
      bg: `bg-${color}-100`,
      text: `text-${color}-800`,
      border: `border-${color}-300`
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'failed': return <XCircle className="w-5 h-5 text-red-600" />
      case 'running': return <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
      default: return <AlertCircle className="w-5 h-5 text-gray-600" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cron Job Management</h1>
          <p className="text-gray-600 mt-1">Manage scheduled tasks and view execution history</p>
        </div>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Job
        </button>
      </div>

      {/* Categories Filter */}
      <div className="mb-6 flex gap-2 flex-wrap">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-4 py-2 rounded-lg ${!selectedCategory ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          All ({categories.reduce((sum, cat) => sum + cat.total_jobs, 0)})
        </button>
        {categories.map(cat => (
          <button
            key={cat.category}
            onClick={() => setSelectedCategory(cat.category)}
            className={`px-4 py-2 rounded-lg ${selectedCategory === cat.category ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {cat.category.replace('_', ' ')} ({cat.total_jobs})
          </button>
        ))}
      </div>

      {/* Jobs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {jobs.map(job => (
              <tr key={job.id} className={!job.enabled ? 'bg-gray-50 opacity-60' : ''}>
                <td className="px-6 py-4">
                  <div>
                    <div className="font-semibold text-gray-900">{job.name}</div>
                    <div className="text-sm text-gray-500">{job.description}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <Clock className="w-4 h-4" />
                      {cronToReadable(job.schedule)}
                    </div>
                    <div className="text-xs text-gray-500 font-mono ml-6">
                      {job.schedule}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                    {job.category}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    {job.last_run_at ? (
                      <>
                        <div className="text-gray-900">
                          {new Date(job.last_run_at).toLocaleString()}
                        </div>
                        <div className="text-gray-500">
                          {job.last_run_duration_seconds}s
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-400">Never</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {job.last_run_status && (
                    <div className="flex items-center gap-2">
                      {getStatusIcon(job.last_run_status)}
                      <span className="text-sm capitalize">{job.last_run_status}</span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <div className={`font-semibold ${job.success_rate >= 90 ? 'text-green-600' : job.success_rate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {job.success_rate.toFixed(1)}%
                    </div>
                    <div className="text-gray-500 text-xs">
                      {job.success_count}/{job.run_count} runs
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => toggleJob(job.id)}
                      className={`p-2 rounded hover:bg-gray-100 ${job.enabled ? 'text-green-600' : 'text-gray-400'}`}
                      title={job.enabled ? 'Disable' : 'Enable'}
                    >
                      {job.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => runJob(job.id)}
                      className="p-2 rounded hover:bg-gray-100 text-blue-600"
                      title="Run Now"
                      disabled={!job.enabled}
                    >
                      <Play className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => viewExecutions(job)}
                      className="p-2 rounded hover:bg-gray-100 text-gray-600"
                      title="View History"
                    >
                      <Clock className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => editJob(job)}
                      className="p-2 rounded hover:bg-gray-100 text-gray-600"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteJob(job.id, job.name)}
                      className="p-2 rounded hover:bg-red-100 text-red-600"
                      title="Delete Job"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Execution History Modal */}
      {showExecutions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">
                Execution History: {selectedJob?.name}
              </h2>
              <button
                onClick={() => setShowExecutions(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="space-y-3">
              {executions.map(exec => (
                <div key={exec.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(exec.status)}
                      <div>
                        <div className="font-semibold capitalize">{exec.status}</div>
                        <div className="text-sm text-gray-500">
                          {new Date(exec.started_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium">{exec.duration_seconds}s</div>
                      <div className="text-xs text-gray-500">Exit: {exec.exit_code}</div>
                    </div>
                  </div>

                  {exec.output && (
                    <div className="mt-2 bg-gray-50 rounded p-2 text-xs font-mono overflow-x-auto">
                      {exec.output.substring(0, 200)}
                      {exec.output.length > 200 && '...'}
                    </div>
                  )}

                  {exec.error && (
                    <div className="mt-2 bg-red-50 rounded p-2 text-xs font-mono text-red-600">
                      {exec.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit Job Modal */}
      {showEditModal && editingJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">Edit Cron Job</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingJob.name}
                  onChange={(e) => setEditingJob({ ...editingJob, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={editingJob.description || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Schedule</label>

                {/* Mode Toggle */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setScheduleMode('preset')}
                    className={`px-3 py-1 rounded text-sm ${scheduleMode === 'preset' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Quick Presets
                  </button>
                  <button
                    onClick={() => setScheduleMode('custom')}
                    className={`px-3 py-1 rounded text-sm ${scheduleMode === 'custom' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Custom Schedule
                  </button>
                </div>

                {/* Preset Mode */}
                {scheduleMode === 'preset' && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button onClick={() => applyPreset('0 0 * * *')} className="p-2 border rounded hover:bg-blue-50 text-left text-sm">
                      <div className="font-medium">Daily at Midnight</div>
                      <div className="text-xs text-gray-500">0 0 * * *</div>
                    </button>
                    <button onClick={() => applyPreset('0 1 * * *')} className="p-2 border rounded hover:bg-blue-50 text-left text-sm">
                      <div className="font-medium">Daily at 1:00 AM</div>
                      <div className="text-xs text-gray-500">0 1 * * *</div>
                    </button>
                    <button onClick={() => applyPreset('0 2 * * *')} className="p-2 border rounded hover:bg-blue-50 text-left text-sm">
                      <div className="font-medium">Daily at 2:00 AM</div>
                      <div className="text-xs text-gray-500">0 2 * * *</div>
                    </button>
                    <button onClick={() => applyPreset('0 3 * * *')} className="p-2 border rounded hover:bg-blue-50 text-left text-sm">
                      <div className="font-medium">Daily at 3:00 AM</div>
                      <div className="text-xs text-gray-500">0 3 * * *</div>
                    </button>
                    <button onClick={() => applyPreset('0 17 * * 1-5')} className="p-2 border rounded hover:bg-blue-50 text-left text-sm">
                      <div className="font-medium">Weekdays at 5:00 PM</div>
                      <div className="text-xs text-gray-500">0 17 * * 1-5</div>
                    </button>
                    <button onClick={() => applyPreset('0 18 * * 1-5')} className="p-2 border rounded hover:bg-blue-50 text-left text-sm">
                      <div className="font-medium">Weekdays at 6:00 PM</div>
                      <div className="text-xs text-gray-500">0 18 * * 1-5</div>
                    </button>
                    <button onClick={() => applyPreset('0 12 * * *')} className="p-2 border rounded hover:bg-blue-50 text-left text-sm">
                      <div className="font-medium">Daily at Noon</div>
                      <div className="text-xs text-gray-500">0 12 * * *</div>
                    </button>
                    <button onClick={() => applyPreset('0 0 * * 0')} className="p-2 border rounded hover:bg-blue-50 text-left text-sm">
                      <div className="font-medium">Weekly on Sunday</div>
                      <div className="text-xs text-gray-500">0 0 * * 0</div>
                    </button>
                  </div>
                )}

                {/* Custom Mode */}
                {scheduleMode === 'custom' && (
                  <div className="space-y-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
                      <select
                        value={customSchedule.frequency}
                        onChange={(e) => setCustomSchedule({ ...customSchedule, frequency: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekdays">Weekdays (Mon-Fri)</option>
                        <option value="weekends">Weekends (Sat-Sun)</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Hour (0-23)</label>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={customSchedule.hour}
                          onChange={(e) => setCustomSchedule({ ...customSchedule, hour: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Minute (0-59)</label>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={customSchedule.minute}
                          onChange={(e) => setCustomSchedule({ ...customSchedule, minute: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                    </div>

                    {customSchedule.frequency === 'weekly' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Day of Week</label>
                        <select
                          value={customSchedule.dayOfWeek}
                          onChange={(e) => setCustomSchedule({ ...customSchedule, dayOfWeek: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="0">Sunday</option>
                          <option value="1">Monday</option>
                          <option value="2">Tuesday</option>
                          <option value="3">Wednesday</option>
                          <option value="4">Thursday</option>
                          <option value="5">Friday</option>
                          <option value="6">Saturday</option>
                        </select>
                      </div>
                    )}

                    {customSchedule.frequency === 'monthly' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Day of Month (1-31)</label>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={customSchedule.dayOfMonth}
                          onChange={(e) => setCustomSchedule({ ...customSchedule, dayOfMonth: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                    )}

                    <button
                      onClick={applyCustomSchedule}
                      className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      Apply Custom Schedule
                    </button>
                  </div>
                )}

                {/* Current Schedule Display */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-xs font-medium text-blue-900 mb-1">Current Schedule:</div>
                  <div className="font-semibold text-blue-900">{cronToReadable(editingJob.schedule)}</div>
                  <div className="text-xs font-mono text-blue-700 mt-1">{editingJob.schedule}</div>
                </div>

                {/* Manual Override */}
                <div className="mt-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Or enter cron expression manually:</label>
                  <input
                    type="text"
                    value={editingJob.schedule}
                    onChange={(e) => setEditingJob({ ...editingJob, schedule: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                    placeholder="0 0 * * *"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Command</label>
                <textarea
                  value={editingJob.command}
                  onChange={(e) => setEditingJob({ ...editingJob, command: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={editingJob.category}
                  onChange={(e) => setEditingJob({ ...editingJob, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="daily_pipeline">Daily Pipeline</option>
                  <option value="ml_training">ML Training</option>
                  <option value="model_management">Model Management</option>
                  <option value="monitoring">Monitoring</option>
                  <option value="infrastructure">Infrastructure</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="general">General</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timeout (seconds)</label>
                <input
                  type="number"
                  value={editingJob.timeout_seconds || 3600}
                  onChange={(e) => setEditingJob({ ...editingJob, timeout_seconds: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={updateJob}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Progress Modal */}
      {showProgressModal && runningJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {jobProgress?.status === 'running' ? 'Running Job...' : 'Job Completed'}
                </h2>
                <p className="text-gray-600">{runningJob.name}</p>
              </div>
              <div className="flex items-center gap-3">
                {jobProgress?.status === 'running' && (
                  <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                )}
                {jobProgress?.status === 'success' && (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                )}
                {jobProgress?.status === 'failed' && (
                  <XCircle className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>

            {/* Status Bar */}
            <div className="mb-4 p-3 rounded-lg bg-gray-50 border">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    jobProgress?.status === 'running' ? 'bg-blue-100 text-blue-800' :
                    jobProgress?.status === 'success' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {jobProgress?.status?.toUpperCase()}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Duration: {jobProgress?.duration || 0}s
                </div>
              </div>
              {jobProgress?.status === 'running' && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
                  </div>
                </div>
              )}
            </div>

            {/* Output */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="text-sm font-medium text-gray-700 mb-2">Output:</div>
              <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-y-auto font-mono text-sm text-green-400 min-h-[200px] max-h-[300px]">
                {jobProgress?.output ? (
                  <pre className="whitespace-pre-wrap">{jobProgress.output}</pre>
                ) : jobProgress?.status === 'running' ? (
                  <span className="text-gray-500">Waiting for output...</span>
                ) : (
                  <span className="text-gray-500">No output</span>
                )}
              </div>
            </div>

            {/* Error */}
            {jobProgress?.error && (
              <div className="mt-4">
                <div className="text-sm font-medium text-red-700 mb-2">Error:</div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 font-mono text-sm text-red-600 max-h-[150px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap">{jobProgress.error}</pre>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              {jobProgress?.status === 'running' ? (
                <button
                  onClick={closeProgressModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  Run in Background
                </button>
              ) : (
                <button
                  onClick={closeProgressModal}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CronJobs
